import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageLoaderContext } from '../../../../../contexts/PageLoaderContext';
import { useNotification } from '../../../../../contexts/NotificationContext';
import { useConfirm } from '../../../../../contexts/ConfirmContext';
import { useAuth } from '../../../../../contexts/AuthContext';
import apiService from '../../../../../services/api';
import CustomDropdown, { DropdownOption } from '../../../../UI/CustomDropdown';
import SidePanel from '../../../../UI/SidePanel';
import TaskForm, { TaskFormData } from '../../../TaskManagement/components/TaskForm';
import SkeletonLoader from '../../../../UI/SkeletonLoader';
import { Task, TaskFilters, TaskStats } from '../../../TaskManagement/types';
import TaskStatsComponent from '../../../TaskManagement/components/TaskStats';
import TaskList from '../../../TaskManagement/components/TaskList';
import { calculateStats } from '../../../TaskManagement/components/helpers';
import { UserPermission } from '../../../../../types';
import styles from '../../../../../styles/TaskManagement.module.css';
import buttonStyles from '../../../../../styles/Buttons.module.css';
import searchStyles from '../../../../../styles/SearchBar.module.css';
import customDropdownStyles from '../../../../../styles/CustomDropdown.module.css';
import sidePanelStyles from '../../../../../styles/SidePanel.module.css';
import skeletonStyles from '../../../../../styles/SkeletonLoader.module.css';
import taskFormStyles from '../../../../../styles/TaskForm.module.css';

interface GroupDetailsData {
  id: string;
  name: string;
  description: string;
  category: {
    id: string;
    name: string;
    color: string;
  };
  team_lead: {
    id: string;
    full_name: string;
    email: string;
  };
  access_level: string;
  is_active: boolean;
  created_at: string;
  member_count: number;
  theme_color: string;
  logo: string | null;
}

interface TaskManagementTabProps {
  groupDetails: GroupDetailsData;
  onGroupUpdate: () => void;
  userPermissions?: any;
  hasPermission?: (permission: string) => boolean;
  isAdminContext?: boolean;
}

const TaskManagementTab: React.FC<TaskManagementTabProps> = ({
  groupDetails,
  onGroupUpdate,
  userPermissions,
  hasPermission = () => false,
  isAdminContext = true
}) => {
  const navigate = useNavigate();
  const notificationService = useNotification();
  const confirmService = useConfirm();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const { user } = useAuth();

  const isWorkMode = true;

  const [filters, setFilters] = useState<TaskFilters>({
    search: '',
    status: '',
    priority: '',
    category: '',
    group: '',
    due_date: ''
  });

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [userPermissionsList, setUserPermissionsList] = useState<UserPermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [hasEditPermissions, setHasEditPermissions] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [taskCategories, setTaskCategories] = useState<any[]>([]);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'global' | 'group'>('all');
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editingTaskDetails, setEditingTaskDetails] = useState<any>(null);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [isLoadingTaskDetails, setIsLoadingTaskDetails] = useState(false);

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'on_hold', label: 'On Hold' }
  ];

  const priorityOptions = [
    { value: '', label: 'All Priority' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];


  useEffect(() => {
    const loadUserPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        if (response && response.status === 'success') {
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as UserPermission[];
          setUserPermissionsList(permissions);

          const editActions = ['add', 'edit', 'delete', 'create', 'update'];
          const hasEdit = permissions.some(
            perm => perm.module === 'task_management' && editActions.includes(perm.action)
          );
          setHasEditPermissions(hasEdit);
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
        setUserPermissionsList([]);
        setHasEditPermissions(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, []);

  const loadTasks = async (isModeSwitch: boolean = false) => {
    if (isLoadingTasks) {
      return;
    }

    setIsLoadingTasks(true);
    setLoading(true);

    if (!isModeSwitch) {
      showLoader('Loading tasks...');
    }

    const startTime = Date.now();
    const minLoadingTime = isModeSwitch ? 1000 : 0;

    try {
      const params: any = {
        search: filters.search || '',
        status: filters.status || '',
        priority: filters.priority || '',
        category: filters.category || '',
        work_mode: isWorkMode ? 'true' : 'false', // Send as string for backend
        exclude_templates: 'false' // Backend will handle filtering based on user role
      };

      const response = await apiService.getGroupTasks(groupDetails.id, params);

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.status === 'success') {
        let tasksData = response.data?.tasks || response.data || [];
        tasksData = Array.isArray(tasksData) ? tasksData : [];
        setTasks(tasksData);
        setError(null);
        setIsInitialLoad(false);
      } else {
        setError(response.message || 'Failed to load tasks');
        notificationService.error(response.message || 'Failed to load tasks');
      }
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      setError(err);
      notificationService.error(err.response?.data?.message || 'Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingTasks(false);

      if (!isModeSwitch) {
        hideLoader();
      }
    }
  };

  // Load tasks on mount and when filters change
  useEffect(() => {
    loadTasks();
  }, [groupDetails.id]); // Load tasks when group changes

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadTasks(true); // Pass isModeSwitch=true to avoid full loader on filter changes
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.search, filters.status, filters.priority, filters.category, filters.due_date]);

  const handleTaskClick = (task: Task) => {
    navigate(`/group-management/groups/${groupDetails.id}/tasks/${task.id}`);
  };

  const handleEditTask = async (task: Task) => {
    setEditingTask(task);
    setShowEditModal(true);
    setIsLoadingTaskDetails(true);

    try {
      const response = await apiService.getTaskDetails(task.id);
      if (response.status === 'success') {
        const taskData = response.data || response;
        setEditingTaskDetails(taskData);
        try {
          const docsResponse = await apiService.getTaskDocuments(groupDetails.id, task.id);
          if (docsResponse.status === 'success') {
            const documentsData = docsResponse.data || (docsResponse as any).documents || [];
            setExistingDocuments(Array.isArray(documentsData) ? documentsData : []);
          }
        } catch (error) {
          console.error('Error loading task documents:', error);
          setExistingDocuments([]);
        }
      } else {
        notificationService.error('Failed to load task details');
      }
    } catch (error: any) {
      console.error('Error loading task details:', error);
      notificationService.error(error.response?.data?.message || 'Failed to load task details');
    } finally {
      setIsLoadingTaskDetails(false);
    }
  };

  const handleDeleteTask = async (task: Task) => {
    const confirmed = await confirmService.show(
      'Delete Task',
      `Are you sure you want to delete the task "${task.name}"? This action cannot be undone.`,
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    showLoader('Deleting task...');
    try {
      const response = await apiService.deleteTask(task.id, groupDetails.id);

      if (response.status === 'success') {
        notificationService.success('Task deleted successfully');
        loadTasks();
      } else {
        notificationService.error(response.message || 'Failed to delete task');
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      notificationService.error(error.response?.data?.message || 'Failed to delete task. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleFilterChange = (key: keyof TaskFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Load task categories with filter support
  const loadTaskCategories = async (filter: 'all' | 'global' | 'group' = categoryFilter) => {
    if (isLoadingCategories) return;

    setIsLoadingCategories(true);
    try {
      // In group TaskManagement, fetch based on filter (all, global, or group)
      const response = await apiService.getTaskCategories(groupDetails.id, filter);

      if (response.success) {
        setTaskCategories(response.categories || []);
      } else {
        notificationService.error(response.message || 'Failed to load task categories');
      }
    } catch (error: any) {
      console.error('Error loading task categories:', error);
      notificationService.error((error as any).response?.data?.message || 'Failed to load task categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // Handle category filter change
  const handleCategoryFilterChange = (filter: 'all' | 'global' | 'group') => {
    setCategoryFilter(filter);
    loadTaskCategories(filter);
  };

  // Load group members and check if user is group admin
  const loadGroupMembers = async () => {
    if (isLoadingMembers) return;

    setIsLoadingMembers(true);
    try {
      const response = await apiService.getGroupMembers(groupDetails.id);
      if (response.status === 'success') {
        const members = response.data || [];
        setGroupMembers(members);

        // Check if current user is group admin
        if (user) {
          const currentUserMember = members.find((m: any) => m.user.id === user.id);
          const isAdmin = currentUserMember
            ? (currentUserMember.role_type === 'admin' || currentUserMember.is_main_admin === true)
            : false;
          setIsGroupAdmin(isAdmin);
        }
      }
    } catch (error: any) {
      console.error('Error loading group members:', error);
      notificationService.error((error as any).response?.data?.message || 'Failed to load group members');
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // Check if user can edit/delete (system admin or group admin)
  const canEditDelete = user?.role === 'admin' || isGroupAdmin;

  // Handle update task submission with files
  const handleUpdateTask = async (formData: TaskFormData, files: File[], documentMetadata: Record<string, any>) => {
    if (!editingTask) return;

    showLoader('Updating task...');
    try {
      // Create FormData for file uploads
      const formDataObj = new FormData();

      formDataObj.append('name', formData.name.trim());
      formDataObj.append('description', formData.description || '');
      formDataObj.append('category_id', formData.category_id);
      formDataObj.append('priority', formData.priority);
      formDataObj.append('assignee_ids', JSON.stringify(formData.assignee_ids));
      formDataObj.append('due_date', formData.due_date);
      formDataObj.append('estimated_hours', formData.estimated_hours.toString());
      formDataObj.append('status', formData.status || 'pending');
      formDataObj.append('approval_type', formData.approval_type);
      formDataObj.append('requires_team_lead_approval', formData.requires_team_lead_approval.toString());
      formDataObj.append('requires_documents', formData.requires_documents.toString());
      formDataObj.append('enable_group_chat', formData.enable_group_chat.toString());
      formDataObj.append('distribution_method', formData.distribution_method);
      formDataObj.append('point_allocation', JSON.stringify(formData.point_allocation));
      // Work descriptions are NOT stored for template tasks
      const workDescriptions = formData.allow_user_instances ? {} : formData.work_descriptions;
      formDataObj.append('work_descriptions', JSON.stringify(workDescriptions));

      // Recurrence/Scheduler fields
      formDataObj.append('is_recurring', formData.is_recurring.toString());
      formDataObj.append('recurrence_type', formData.recurrence_type);
      if (formData.recurrence_weekly_days && formData.recurrence_weekly_days.length > 0) {
        formDataObj.append('recurrence_weekly_days', JSON.stringify(formData.recurrence_weekly_days));
      }
      if (formData.recurrence_monthly_dates && formData.recurrence_monthly_dates.length > 0) {
        formDataObj.append('recurrence_monthly_dates', JSON.stringify(formData.recurrence_monthly_dates));
      }
      if (formData.recurrence_yearly_date) {
        formDataObj.append('recurrence_yearly_date', JSON.stringify(formData.recurrence_yearly_date));
      }
      if (formData.recurrence_end_date) {
        formDataObj.append('recurrence_end_date', formData.recurrence_end_date);
      }

      // User instance creation fields
      formDataObj.append('allow_user_instances', formData.allow_user_instances.toString());
      if (formData.instance_template_name) {
        formDataObj.append('instance_template_name', formData.instance_template_name);
      }

      // Add uploaded files
      files.forEach((file, index) => {
        formDataObj.append(`document_${index}`, file);
        const metadata = documentMetadata[Object.keys(documentMetadata)[index]];
        if (metadata) {
          formDataObj.append(`document_title_${index}`, metadata.title);
          formDataObj.append(`document_description_${index}`, metadata.description);
          formDataObj.append(`document_type_${index}`, metadata.document_type);
          formDataObj.append(`document_required_${index}`, metadata.is_required.toString());
        }
      });

      // Add approver IDs if approval is required (single or multiple)
      if (formData.approval_type && formData.approval_type !== 'none' && formData.approver_ids && formData.approver_ids.length > 0) {
        formDataObj.append('approver_ids', JSON.stringify(formData.approver_ids));
      }

      const response = await apiService.updateTaskWithFiles(editingTask.id, groupDetails.id, formDataObj);

      if (response.status === 'success') {
        notificationService.success('Task updated successfully');
        setShowEditModal(false);
        setEditingTask(null);
        setTaskCategories([]);
        setGroupMembers([]);
        loadTasks();
        onGroupUpdate();
      } else {
        notificationService.error(response.message || 'Failed to update task');
      }
    } catch (error: any) {
      console.error('Error updating task:', error);
      notificationService.error(error.response?.data?.message || 'Failed to update task. Please try again.');
    } finally {
      hideLoader();
    }
  };

  // Handle form submission with files
  const handleFormSubmit = async (formData: TaskFormData, files: File[], documentMetadata: Record<string, any>) => {
    showLoader('Creating task...');
    try {
      // Create FormData for file uploads
      const formDataObj = new FormData();

      formDataObj.append('name', formData.name.trim());
      formDataObj.append('description', formData.description || '');
      formDataObj.append('category_id', formData.category_id);
      formDataObj.append('priority', formData.priority);
      // For template tasks, assignees represent who can create instances (still send them)
      // Point allocation rules are configured for template tasks
      // Work descriptions are NOT stored for template tasks
      formDataObj.append('assignee_ids', JSON.stringify(formData.assignee_ids));
      formDataObj.append('due_date', formData.due_date);
      formDataObj.append('estimated_hours', formData.estimated_hours.toString());
      formDataObj.append('approval_type', formData.approval_type);
      formDataObj.append('requires_team_lead_approval', formData.requires_team_lead_approval.toString());
      formDataObj.append('requires_documents', formData.requires_documents.toString());
      formDataObj.append('enable_group_chat', formData.enable_group_chat.toString());
      formDataObj.append('distribution_method', formData.distribution_method);
      // Point allocation is configured for template tasks (rules apply to instances)
      formDataObj.append('point_allocation', JSON.stringify(formData.point_allocation));
      // Work descriptions are NOT stored for template tasks
      const workDescriptions = formData.allow_user_instances ? {} : formData.work_descriptions;
      formDataObj.append('work_descriptions', JSON.stringify(workDescriptions));

      // Recurrence/Scheduler fields
      formDataObj.append('is_recurring', formData.is_recurring.toString());
      formDataObj.append('recurrence_type', formData.recurrence_type);
      if (formData.recurrence_weekly_days && formData.recurrence_weekly_days.length > 0) {
        formDataObj.append('recurrence_weekly_days', JSON.stringify(formData.recurrence_weekly_days));
      }
      if (formData.recurrence_monthly_dates && formData.recurrence_monthly_dates.length > 0) {
        formDataObj.append('recurrence_monthly_dates', JSON.stringify(formData.recurrence_monthly_dates));
      }
      if (formData.recurrence_yearly_date) {
        formDataObj.append('recurrence_yearly_date', JSON.stringify(formData.recurrence_yearly_date));
      }
      if (formData.recurrence_end_date) {
        formDataObj.append('recurrence_end_date', formData.recurrence_end_date);
      }

      // User instance creation fields
      formDataObj.append('allow_user_instances', formData.allow_user_instances.toString());
      if (formData.instance_template_name) {
        formDataObj.append('instance_template_name', formData.instance_template_name);
      }

      // Add uploaded files
      files.forEach((file, index) => {
        formDataObj.append(`document_${index}`, file);
        const metadata = documentMetadata[Object.keys(documentMetadata)[index]];
        if (metadata) {
          formDataObj.append(`document_title_${index}`, metadata.title);
          formDataObj.append(`document_description_${index}`, metadata.description);
          formDataObj.append(`document_type_${index}`, metadata.document_type);
          formDataObj.append(`document_required_${index}`, metadata.is_required.toString());
        }
      });

      // Add approver IDs if approval is required (single or multiple)
      if (formData.approval_type && formData.approval_type !== 'none' && formData.approver_ids && formData.approver_ids.length > 0) {
        formDataObj.append('approver_ids', JSON.stringify(formData.approver_ids));
      }

      const response = await apiService.createTaskWithFiles(groupDetails.id, formDataObj);

      if (response.status === 'success') {
        notificationService.success('Task created successfully');
        setShowAddModal(false);
        setTaskCategories([]);
        setGroupMembers([]);
        loadTasks();
        onGroupUpdate();
      } else {
        notificationService.error(response.message || 'Failed to create task');
      }
    } catch (error: any) {
      console.error('Error creating task:', error);
      notificationService.error(error.response?.data?.message || 'Failed to create task. Please try again.');
    } finally {
      hideLoader();
    }
  };

  // Load group members on mount to check admin status
  useEffect(() => {
    loadGroupMembers();
  }, [groupDetails.id, user]);

  // Load categories and members when modal opens
  useEffect(() => {
    if (showAddModal) {
      if (taskCategories.length === 0) {
        loadTaskCategories(categoryFilter);
      }
      if (groupMembers.length === 0) {
        loadGroupMembers();
      }
    } else {
      // Reset state when modal closes
      setTaskCategories([]);
      setCategoryFilter('all');
    }
  }, [showAddModal]);

  // Load categories and members when edit modal opens
  useEffect(() => {
    if (showEditModal) {
      if (taskCategories.length === 0) {
        loadTaskCategories(categoryFilter);
      }
      if (groupMembers.length === 0) {
        loadGroupMembers();
      }
    } else {
      // Reset state when edit modal closes
      if (!showAddModal) {
        setTaskCategories([]);
        setCategoryFilter('all');
      }
      setEditingTaskDetails(null);
      setExistingDocuments([]);
    }
  }, [showEditModal]);

  const stats: TaskStats = calculateStats(tasks);

  return (
    <div className={styles.taskManagementContainer}>
      {/* Normal Header */}
      <div className={styles.headerContent} style={{ position: 'relative', padding: '0 1rem', marginBottom: '1.5rem' }}>
        <div className={styles.headerRow}>
          <div className={styles.searchWrapper}>
            <div className={searchStyles.searchBar}>
              <input
                type="text"
                placeholder="Search tasks..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
              <button type="submit">
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.headerActions}>
            {/* Show Create Task button if user has permissions */}
            {hasEditPermissions && (
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={() => setShowAddModal(true)}
                title="Create Task"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span className={styles.btnText}>Create Task</span>
              </button>
            )}
            <CustomDropdown
              options={statusOptions}
              value={filters.status}
              onChange={(value: string | string[]) => handleFilterChange('status', Array.isArray(value) ? value[0] : value)}
              placeholder="All Status"
              size="sm"
              variant="outlined"
              className="filter"
            />
            <CustomDropdown
              options={priorityOptions}
              value={filters.priority}
              onChange={(value: string | string[]) => handleFilterChange('priority', Array.isArray(value) ? value[0] : value)}
              placeholder="All Priority"
              size="sm"
              variant="outlined"
              className="filter"
            />
            <div className={styles.dateFilterWrapper}>
              <input
                type="date"
                className={styles.dateFilterInput}
                value={filters.due_date}
                onChange={(e) => handleFilterChange('due_date', e.target.value)}
                placeholder="Due date"
              />
              {filters.due_date && (
                <button
                  className={styles.dateFilterClear}
                  onClick={() => handleFilterChange('due_date', '')}
                  title="Clear date filter"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
            <button
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={() => loadTasks()}
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6"></path>
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                <path d="M3 22v-6h6"></path>
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
              </svg>
              <span className={styles.btnText}>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div style={{ padding: '0' }}>
        {/* Stats */}
        <TaskStatsComponent stats={stats} isWorkMode={true} />

        {/* Tasks List */}
        <TaskList
          tasks={tasks}
          loading={loading}
          error={error}
          isInitialLoad={isInitialLoad}
          onTaskClick={handleTaskClick}
          onRetry={loadTasks}
          isWorkMode={true}
          onEdit={canEditDelete ? handleEditTask : undefined}
          onDelete={canEditDelete ? handleDeleteTask : undefined}
          showEditDelete={canEditDelete}
        />
      </div>

      {/* Create Task Modal */}
      {showAddModal && (
        <SidePanel
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setTaskCategories([]);
            setGroupMembers([]);
            setCategoryFilter('all');
          }}
          title="Create New Task"
        >
          <div className={sidePanelStyles.form}>
            {/* Task Form Component */}
            <TaskForm
              groupId={groupDetails.id}
              taskCategories={taskCategories}
              groupMembers={groupMembers}
              isLoadingCategories={isLoadingCategories}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowAddModal(false);
                setTaskCategories([]);
                setGroupMembers([]);
                setCategoryFilter('all');
              }}
              mode="create"
              showCategoryFilter={true}
              categoryFilter={categoryFilter}
              onCategoryFilterChange={handleCategoryFilterChange}
            />
          </div>
        </SidePanel>
      )}

      {/* Edit Task Modal */}
      {showEditModal && editingTask && (
        <SidePanel
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingTask(null);
            setEditingTaskDetails(null);
            setExistingDocuments([]);
            setTaskCategories([]);
            setGroupMembers([]);
            setCategoryFilter('all');
          }}
          title="Edit Task"
        >
          <div className={sidePanelStyles.form}>
            {isLoadingTaskDetails ? (
              <div className={`${sidePanelStyles.form} ${taskFormStyles.formContainer}`} style={{ pointerEvents: 'none' }}>
                {/* Step Indicator Skeleton */}
                <div className={taskFormStyles.stepIndicator}>
                  {[1, 2, 3].map((stepNum, index) => (
                    <React.Fragment key={stepNum}>
                      <div className={taskFormStyles.stepItem}>
                        <div className={skeletonStyles.skeletonCustom} style={{ width: '40px', height: '40px', borderRadius: '50%', marginBottom: '0.5rem' }}>
                          <div className={skeletonStyles.skeletonShimmer}></div>
                        </div>
                        <div className={taskFormStyles.stepInfo} style={{ marginTop: '0.5rem' }}>
                          <div className={skeletonStyles.skeletonLine} style={{ width: '120px', height: '14px', marginBottom: '0.25rem' }}></div>
                        </div>
                      </div>
                      {index < 2 && (
                        <div className={taskFormStyles.stepConnector}>
                          <div className={skeletonStyles.skeletonLine} style={{ width: '100%', height: '2px' }}></div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Form Content Skeleton */}
                <div className={taskFormStyles.stepContent} style={{ marginTop: '2rem' }}>
                  {/* Task Name */}
                  <div className={sidePanelStyles.formGroup}>
                    <div className={skeletonStyles.skeletonLine} style={{ width: '25%', height: '16px', marginBottom: '0.5rem' }}></div>
                    <SkeletonLoader variant="custom" count={1} height="44px" />
                  </div>

                  {/* Description */}
                  <div className={sidePanelStyles.formGroup}>
                    <div className={skeletonStyles.skeletonLine} style={{ width: '30%', height: '16px', marginBottom: '0.5rem' }}></div>
                    <SkeletonLoader variant="custom" count={1} height="80px" />
                  </div>

                  {/* Category and Priority Row */}
                  <div className={sidePanelStyles.formRow}>
                    <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                      <div className={skeletonStyles.skeletonLine} style={{ width: '35%', height: '16px', marginBottom: '0.5rem' }}></div>
                      <SkeletonLoader variant="custom" count={1} height="44px" />
                    </div>
                    <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                      <div className={skeletonStyles.skeletonLine} style={{ width: '40%', height: '16px', marginBottom: '0.5rem' }}></div>
                      <SkeletonLoader variant="custom" count={1} height="44px" />
                    </div>
                  </div>

                  {/* Due Date and Estimated Hours Row */}
                  <div className={sidePanelStyles.formRow}>
                    <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                      <div className={skeletonStyles.skeletonLine} style={{ width: '30%', height: '16px', marginBottom: '0.5rem' }}></div>
                      <SkeletonLoader variant="custom" count={1} height="44px" />
                    </div>
                    <div className={sidePanelStyles.formGroup} style={{ flex: 1 }}>
                      <div className={skeletonStyles.skeletonLine} style={{ width: '40%', height: '16px', marginBottom: '0.5rem' }}></div>
                      <SkeletonLoader variant="custom" count={1} height="44px" />
                    </div>
                  </div>
                </div>

                {/* Step Navigation Skeleton */}
                <div className={`${sidePanelStyles.formActions} ${taskFormStyles.stepNavigation}`} style={{ marginTop: '2rem' }}>
                  <div className={taskFormStyles.navigationLeft}>
                    <SkeletonLoader variant="custom" count={1} height="40px" width="100px" />
                  </div>
                  <div className={taskFormStyles.navigationRight}>
                    <SkeletonLoader variant="custom" count={1} height="40px" width="120px" />
                  </div>
                </div>
              </div>
            ) : editingTaskDetails ? (
              <TaskForm
                groupId={groupDetails.id}
                taskCategories={taskCategories}
                groupMembers={groupMembers}
                isLoadingCategories={isLoadingCategories}
                onSubmit={handleUpdateTask}
                onCancel={() => {
                  setShowEditModal(false);
                  setEditingTask(null);
                  setEditingTaskDetails(null);
                  setExistingDocuments([]);
                  setTaskCategories([]);
                  setGroupMembers([]);
                  setCategoryFilter('all');
                }}
                mode="edit"
                initialData={{
                  name: editingTaskDetails.name || '',
                  description: editingTaskDetails.description || '',
                  category_id: editingTaskDetails.category?.id || '',
                  priority: editingTaskDetails.priority || 'medium',
                  assignee_ids: editingTaskDetails.assignees?.map((a: any) => a.user?.id || a.id) || [],
                  due_date: editingTaskDetails.due_date ? editingTaskDetails.due_date.split('T')[0] : '',
                  estimated_hours: editingTaskDetails.estimated_hours || 0,
                  status: editingTaskDetails.status || 'pending',
                  approval_type: editingTaskDetails.approval_type || (editingTaskDetails.requires_approval ? 'single' : 'none'),
                  approver_ids: editingTaskDetails.approver_ids || [],
                  approvers: editingTaskDetails.approvers || [], // Pass full approver details
                  requires_documents: editingTaskDetails.requires_documents || false,
                  enable_group_chat: editingTaskDetails.enable_group_chat || false,
                  distribution_method: editingTaskDetails.distribution_method || 'equal',
                  direct_assignment_type: editingTaskDetails.direct_assignment_type || 'single',
                  base_points: editingTaskDetails.base_points || 0,
                  team_lead_points: editingTaskDetails.team_lead_points || 0,
                  point_allocation: editingTaskDetails.point_allocation || {},
                  work_descriptions: editingTaskDetails.assignees?.reduce((acc: any, a: any) => {
                    const userId = a.user?.id || a.id;
                    if (userId && a.work_description) {
                      acc[userId] = a.work_description;
                    }
                    return acc;
                  }, {}) || {},
                  is_recurring: editingTaskDetails.is_recurring || false,
                  recurrence_type: editingTaskDetails.recurrence_type || 'none',
                  recurrence_weekly_days: editingTaskDetails.recurrence_weekly_days || [],
                  recurrence_monthly_dates: editingTaskDetails.recurrence_monthly_dates || [],
                  recurrence_yearly_date: editingTaskDetails.recurrence_yearly_date || { month: 1, day: 1 },
                  recurrence_end_date: editingTaskDetails.recurrence_end_date ? editingTaskDetails.recurrence_end_date.split('T')[0] : '',
                  allow_user_instances: editingTaskDetails.allow_user_instances || false,
                  instance_template_name: editingTaskDetails.instance_template_name || '',
                }}
                existingDocuments={existingDocuments}
                onDeleteDocument={async (documentId: string) => {
                  try {
                    const response = await apiService.deleteTaskDocument(groupDetails.id, editingTask.id, documentId);
                    if (response.status === 'success') {
                      setExistingDocuments(prev => prev.filter(doc => doc.id !== documentId));
                      notificationService.success('Document deleted successfully');
                    } else {
                      notificationService.error(response.message || 'Failed to delete document');
                    }
                  } catch (error: any) {
                    console.error('Error deleting document:', error);
                    notificationService.error('Failed to delete document');
                  }
                }}
                onViewDocument={(doc: any) => {
                  if (doc.file_url) {
                    window.open(doc.file_url, '_blank');
                  }
                }}
                onDownloadDocument={(doc: any) => {
                  if (doc.file_url) {
                    const link = document.createElement('a');
                    link.href = doc.file_url;
                    link.download = doc.filename || doc.title;
                    link.click();
                  }
                }}
                showCategoryFilter={true}
                categoryFilter={categoryFilter}
                onCategoryFilterChange={handleCategoryFilterChange}
              />
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Failed to load task details. Please try again.</p>
              </div>
            )}
          </div>
        </SidePanel>
      )}

      {/* Note: No FloatingWorkingMode in group TaskManagement - always in work mode */}
    </div>
  );
};

export default TaskManagementTab;